/**
 * Front-end-only presentation + routing config for the V2 navigation menu.
 *
 * The backend feeds a permission-correct tree of legacy module ids and actions
 * (see src/api/menu.ts). Two things stay on the client because they are pure
 * V2 concerns:
 *
 *   1. Which module gets which icon (cosmetic).
 *   2. Which legacy actions already have a NATIVE V2 route, and where the rest
 *      go until they are ported.
 *
 * As modules get rebuilt in Vue, add their action -> path entry to V2_ROUTES and
 * they automatically switch from "opens the classic app" to an in-SPA route.
 */

/** Lucide icon name per top-level module id. Fallback is `circle-dot`. */
export const MODULE_ICONS: Record<string, string> = {
    racuni:          "file-text",
    partnerji:       "users",
    knjizenje:       "book-open",
    place:           "banknote",
    blagajna:        "calculator",
    materialno:      "package",
    osnovnaSredstva: "warehouse",
    potniNalogi:     "plane",
    ostaliPrejemki:  "receipt",
    placilniPromet:  "arrow-left-right",
    davki:           "percent",
    izpisi:          "bar-chart-3",
    obdelave:        "settings",
    sifranti:        "list",
    admin:           "shield",
    pregled:         "layout-dashboard",
    crm:             "contact",
};

export function moduleIcon(moduleId: string): string {
    return MODULE_ICONS[moduleId] ?? "circle-dot";
}

/**
 * Legacy action (componentName) -> native V2 SPA route.
 *
 * Only actions listed here render as in-app <RouterLink>s; everything else is
 * treated as still-classic and bridged via legacyHref(). Keep keys equal to the
 * `action` field the API returns (which falls back to the menu id when the
 * HEAD_MENU row has no explicit HM_MENU_AKCIJA).
 */
export const V2_ROUTES: Record<string, string> = {
    // Issued invoices — the first fully-native V2 screen.
    racuni_izdaniRacuni:    "/invoices",
    knjizenje_izdaniRacuni: "/invoices",
    // The menu sends HEAD_MENU.HM_MENU_AKCIJA, which differs from the menu id for these rows, so the keys above never matched.
    racuni_izdaniRacuni_seznamIzdanihRacunov: "/invoices",
    // Issued offers share the invoice screens (kind=ponudbe).
    racuni_ponudbe_seznamIzdanihPonudb:       "/invoices?kind=ponudbe",
    v2_ponudbe:                               "/invoices?kind=ponudbe",
    knjizenjeV2_izdaniRacuni:                 "/invoices",

    // V2 screens, reached from a HEAD_MENU row whose id/action is the screen's right (granted in
    // PRAVICE_UPORABNIKOV like any menu item). They used to be pinned at the top of the sidebar.
    v2_adminPregledPf:      "/dashboard",
    v2_crm:                 "/crm",
    v2_admin:               "/admin",
    v2_adminPodjetja:       "/admin/companies",

    // Import of the AJPES annual report (sole proprietor) into the balance sheet and income statement.
    v2_bilanceUvozSp:       "/bilance/uvoz",

    // Stored balance sheet and income statement of the selected company.
    v2_bilancePregled:      "/bilance/pregled",

    // V2 pairs of classic menu rows (HEAD_MENU.HM_V2_AKCIJA): one list screen serves every kind of invoice.
    v2_izdaniRacuni:        "/invoices?kind=racuni",
    v2_gotovinskiRacuni:    "/invoices?kind=gotovinski",
    v2_prejetiRacuni:       "/invoices?kind=prejeti",
    v2_masovniRacuni:       "/invoices?kind=masovni",
    v2_partnerji:           "/partners",

    // Travel-order šifranti. Keyed by the classic action itself, so the V2 sidebar and the V1 switch button
    // (HM_V2_AKCIJA = the same key) both land on the native screen.
    sifranti_vozila:        "/vehicles",
    sifranti_kilometrine:   "/mileage",
    // Payment batches for the bank.
    placilniPrometV2_placila_seznamPlacil: "/payments",
    // Travel orders.
    potniNalogi_seznamPotnihNalogovV2: "/travel-orders",
    // Manual open-item ledger.
    placilniPromet_rocniSK: "/manual-ledger",
    // General-ledger reports (HM_MENU_AKCIJA is empty, so the action is the menu id).
    izpisi_brutobilancaV2:  "/trial-balance",
    izpisi_kontokarticeV2:  "/account-cards",

    // V2 pairs (HEAD_MENU.HM_V2_AKCIJA) of the three screens above.
    v2_vozila:              "/vehicles",
    v2_kilometrine:         "/mileage",
    v2_placila:             "/payments",
    v2_potniNalogi:         "/travel-orders",
    v2_rocniSK:             "/manual-ledger",
    v2_brutoBilanca:        "/trial-balance",
    v2_kontokartice:        "/account-cards",

    // Partner lists: all contact persons, and the subscribers to e-invoices (HM_MENU_AKCIJA is empty, so the action is the menu id).
    partnerji_kontaktneOsebe:     "/partner-contacts",
    partnerji_eRacuniNarocniki:   "/einvoice-subscribers",
    v2_kontaktneOsebe:            "/partner-contacts",
    v2_eracuniNarocniki:          "/einvoice-subscribers",

    // Šifranti of articles (HM_MENU_AKCIJA is empty, so the action is the menu id).
    sifranti_skupineMaterialBlagoStoritve: "/item-groups",
    sifranti_materialBlagoStoritve2:       "/articles",
    sifranti_klavzule:                     "/clauses",
    sifranti_poste:                        "/postal-codes",
    v2_skupineArtiklov:                    "/item-groups",
    v2_artikli:                            "/articles",
    v2_klavzule:                           "/clauses",
    v2_poste:                              "/postal-codes",
};

/**
 * Screens ported for "Sam svoj računovodja" companies (PAKET_ID = 1) only; every other company
 * keeps the classic screen behind the same menu entry.
 */
export const SSR_ROUTES: Record<string, string> = {
    partnerji_seznamPartnerjev: "/partners",
};

let ssrCompany = false;

/** Set by the menu store once it knows the selected company is a "Sam svoj računovodja" one. */
export function setSsrCompany(on: boolean): void {
    ssrCompany = on;
}

/** True when this action has been ported to a native V2 route. */
export function isNativeRoute(action: string): boolean {
    return nativeRoute(action) !== undefined;
}

/** The native V2 path for an action, or undefined if it is still classic. */
export function nativeRoute(action: string): string | undefined {
    if (ssrCompany && Object.prototype.hasOwnProperty.call(SSR_ROUTES, action)) return SSR_ROUTES[action];
    return Object.prototype.hasOwnProperty.call(V2_ROUTES, action) ? V2_ROUTES[action] : undefined;
}

/**
 * In-SPA route that hosts a not-yet-ported classic component in an iframe.
 * Handled by EmbedView, which frames /ep/window-open/<component> — the classic
 * app's existing chromeless single-component mode (no header, no YUI menubar,
 * just the component in its own desktop). The V2 sidebar/topbar stay around it.
 */
export function embedPath(action: string): string {
    return `/legacy/${encodeURIComponent(action)}`;
}

/** Absolute URL of the classic chromeless host for an action (iframe src). */
export function legacyEmbedSrc(component: string): string {
    return `/ep/window-open/${encodeURIComponent(component)}`;
}

/**
 * The in-app path a menu action should navigate to: its native V2 route when
 * ported, otherwise the iframe-embed route. Both are <RouterLink> targets, so
 * clicking any menu item only swaps the content pane — the shell never reloads.
 */
export function targetPath(action: string): string {
    return nativeRoute(action) ?? embedPath(action);
}
